import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { LANDING } from '@/constants/testIds';

const phases = [
    {
        n: '01',
        label: 'El Antes',
        title: 'Preparación Estratégica',
        desc: 'Antes de subirte al avión ya sabes con quién te vas a sentar, qué le vas a ofrecer y cuánto puede valer.',
        list: [
            'Diagnóstico y objetivos comerciales',
            'Selección de mercados prioritarios',
            'Pitch afinado a estándar europeo',
            'Agenda B2B pre-armada',
            'Onboarding a la Tribu',
        ],
        img: 'https://customer-assets-39nsmqrw.emergentagent.net/job_landing-hub-764/artifacts/e9zs9mbn_475256697_18265735585258760_2652486111200570951_n.jpg',
        testId: LANDING.phaseOne,
    },
    {
        n: '02',
        label: 'El Durante',
        title: 'Ejecución en Madrid',
        desc: 'Cinco días con la Tribu ejecutando lo planeado. Nada al azar, nada improvisado, nada de caminar 9 pabellones sin foco.',
        list: [
            'Navegación estratégica del pabellón',
            'Reuniones B2B pre-agendadas',
            'Eventos paralelos e innovación',
            'Networking curado con la Tribu',
            'Revisión diaria para ajustar la estrategia',
        ],
        img: 'https://customer-assets-39nsmqrw.emergentagent.net/job_landing-hub-764/artifacts/6xs21b52_1-1000x565-1-2.jpeg',
        testId: LANDING.phaseTwo,
    },
    {
        n: '03',
        label: 'El Después',
        title: 'Cierre y Escala',
        desc: 'La feria no cierra deals — el seguimiento sí. Vuelves con un roadmap de 90 días y con la Tribu al lado.',
        list: [
            'Sistematización de contactos con IA',
            'Roadmap de seguimiento 90 días',
            'Nurturing de conversaciones clave',
            'Recomendaciones de escala inmediata',
        ],
        img: 'https://customer-assets-39nsmqrw.emergentagent.net/job_landing-hub-764/artifacts/cg493776_475070332_18265735597258760_1271689019046155121_n.jpg',
        testId: LANDING.phaseThree,
    },
];

const PhaseBlock = ({ p, i }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
    const reverse = i % 2 === 1;

    return (
        <div
            ref={ref}
            data-testid={p.testId}
            className="grid grid-cols-12 gap-5 md:gap-8 py-14 md:py-24 border-t border-white/10"
        >
            <motion.div
                initial={{ opacity: 0, x: reverse ? 40 : -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8 }}
                className={`col-span-12 md:col-span-6 ${reverse ? 'md:order-2' : ''} md:self-center`}
            >
                <div className="flex items-center gap-4 mb-6">
                    <span className="num-display text-[color:var(--brand)] text-4xl">{p.n}</span>
                    <span className="eyebrow-brand">Fase · {p.label}</span>
                </div>
                <h3 className="font-display text-white text-4xl md:text-6xl lg:text-7xl mb-8">
                    {p.title}
                </h3>
                <p className="text-white/80 text-lg leading-relaxed max-w-[48ch] mb-10">{p.desc}</p>
                <ul className="space-y-5">
                    {p.list.map((item, k) => (
                        <motion.li
                            key={k}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.15 + k * 0.06 }}
                            className="flex items-start gap-3 text-white/90"
                        >
                            <span className="mt-2.5 h-1 w-4 flex-shrink-0 bg-[color:var(--brand)]" />
                            <span className="text-base md:text-lg leading-relaxed">{item}</span>
                        </motion.li>
                    ))}
                </ul>
            </motion.div>

            <motion.figure
                style={{ y }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 1 }}
                className={`col-span-12 md:col-span-5 ${reverse ? 'md:col-start-1 md:order-1' : 'md:col-start-8'} relative md:self-center`}
            >
                <div className="relative aspect-[3/4] md:aspect-[4/5] w-full overflow-hidden">
                    <img
                        src={p.img}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover cinema-img"
                    />
                    <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,9,8,0) 40%, rgba(10,9,8,0.85) 100%)' }} />
                    <div className="pointer-events-none absolute inset-0 border border-white/10" />
                    <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/80">
                        <span className="font-semibold">Fase {p.n}</span>
                        <span>{p.label}</span>
                    </div>
                </div>
            </motion.figure>
        </div>
    );
};

const Phases = () => (
    <section id="fases" className="relative py-14 md:py-18 border-t border-white/10 bg-[color:var(--surface)]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="mb-8">
                <div className="eyebrow-brand mb-6">Cómo funciona la misión</div>
                <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl max-w-[20ch]">
                    Un sistema de <span className="italic-accent text-[color:var(--brand)] font-normal">3 fases</span> probado.
                </h2>
            </div>
            {phases.map((p, i) => (
                <PhaseBlock key={p.n} p={p} i={i} />
            ))}
        </div>
    </section>
);

export default Phases;
